import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ImageUp, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/states';
import { api } from '@/lib/api';

/**
 * CR-007 (anh Tiến 07/10/2026 mục 4): chữ ký NV trong hồ sơ, in ở ô "Người lập báo giá" trên PDF / Excel.
 * base = '/me' (tự sửa) hoặc '/users/:id' (quản trị). Ảnh thu nhỏ ≤ 600px trên trình duyệt; PNG giữ nền trong.
 */
async function toBase64(file: File): Promise<string> {
  if (!/^image\/(jpeg|png|webp|gif|bmp)$/.test(file.type)) throw new Error('Chỉ nhận file ảnh (PNG, JPG…)');
  const bitmap = await createImageBitmap(file).catch(() => {
    throw new Error('Không đọc được ảnh');
  });
  try {
    const ratio = Math.min(1, 600 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * ratio));
    canvas.height = Math.max(1, Math.round(bitmap.height * ratio));
    const ctx = canvas.getContext('2d')!;
    const png = file.type === 'image/png';
    if (!png) {
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, png ? 'image/png' : 'image/jpeg', 0.9));
    if (!blob) throw new Error('Trình duyệt không xử lý được ảnh');
    if (blob.size > 512 * 1024) throw new Error('Ảnh chữ ký quá lớn sau khi thu nhỏ (tối đa 512 KB)');
    const buf = new Uint8Array(await blob.arrayBuffer());
    let bin = '';
    for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
    return btoa(bin);
  } finally {
    bitmap.close();
  }
}

export function SignatureField({ base, hasSignature, onChanged }: { base: string; hasSignature: boolean; onChanged?: (has: boolean) => void }) {
  const qc = useQueryClient();
  const key = ['signature', base];
  const q = useQuery({ queryKey: key, queryFn: () => api.blob(`${base}/signature`), enabled: hasSignature, staleTime: 60_000 });
  const [src, setSrc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!q.data || !hasSignature) return setSrc(null);
    const u = URL.createObjectURL(q.data);
    setSrc(u);
    return () => URL.revokeObjectURL(u);
  }, [q.data, hasSignature]);

  const run = async (fn: () => Promise<unknown>, has: boolean, msg: string) => {
    setBusy(true);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: key });
      onChanged?.(has);
      toast.success(msg);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Không lưu được chữ ký');
    } finally {
      setBusy(false);
    }
  };
  const upload = (f: File) => run(async () => api.put(`${base}/signature`, { data: await toBase64(f) }), true, 'Đã lưu chữ ký');

  return (
    <div>
      <Label>Chữ ký (in trên báo giá)</Label>
      <div className="flex items-center gap-3 rounded-md border bg-muted/30 p-2">
        <div className="flex h-16 w-44 items-center justify-center rounded border border-dashed bg-white" data-testid="signature-preview">
          {hasSignature && src ? <img src={src} alt="Chữ ký" className="max-h-14 max-w-40 object-contain" /> : <span className="text-xs text-muted-foreground">Chưa có chữ ký</span>}
        </div>
        <div className="flex flex-col gap-1.5">
          <input
            ref={input}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            data-testid="signature-file"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void upload(f);
            }}
          />
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => input.current?.click()}>
            {busy ? <Spinner /> : <ImageUp />} {hasSignature ? 'Đổi ảnh' : 'Tải ảnh chữ ký'}
          </Button>
          {hasSignature && (
            <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => confirm('Xóa chữ ký?') && void run(() => api.del(`${base}/signature`), false, 'Đã xóa chữ ký')}>
              <Trash2 /> Xóa
            </Button>
          )}
        </div>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">Ảnh PNG nền trong suốt in đẹp nhất. Chữ ký và số điện thoại in ở ô "Người lập báo giá".</p>
    </div>
  );
}
